import React from 'react';
import {
  DetailStack, FontWeight, HStack, Text,
  SimpleTable,
} from '@doveit/bricks';
import { Feedback } from '../../../providers/api/dtos';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { CELL_DATE_WIDTH } from '../../../constants';
import DateViewer from '../../../components/date-viewer/DateViewer';
import FeedbackAvatar from '../../components/feedback-avatar/FeedbackAvatar';
import { FeedbackStatus, ReferenceType } from '../../../domain/types';
import useProspect from '../../../prospect/hooks/use-prospect/useProspect';
import ProspectPreview from '../../../prospect/components/prospect-preview/ProspectPreview';
import { ViewFeedbackAction } from '../view-feedback-action/ViewFeedbackAction';
import ShortRating from '../../../components/short-rating/ShortRating';
import UpdateFeedbackStatus from '../update-feedback-status/UpdateFeedbackStatus';

export interface ViewFeedbackTableRowProps extends React.AriaAttributes {
  feedback: Feedback,
  canSeeFeedback?: boolean,
  canUpdateStatus?: boolean,
  onSurveyUpdated?: (feedback: Feedback) => void,
}

const ViewFeedbackTableRow: React.FC<ViewFeedbackTableRowProps> = ({
  feedback,
  canSeeFeedback = false,
  canUpdateStatus = false,
  onSurveyUpdated,
  ...rest
}) => {
  const { data: prospect } = useProspect(feedback.reference?.type === ReferenceType.PROSPECT
    ? feedback.reference.id
    : undefined);

  return (
    <SimpleTable.Row
      aria-label="Informazioni survey"
      {...rest}
    >
      <SimpleTable.Cell
        aria-label="Preview"
        width="12rem"
      >
        <HStack gap={100} wrap="nowrap">
          <FeedbackAvatar size="S" />
          <Text.BodySmall
            aria-label="Titolo survey"
            fontWeight={FontWeight.MEDIUM}
            css={setVerticalEllipsis()}
          >
            {feedback.survey?.name || 'Survey'}
          </Text.BodySmall>
        </HStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Stato"
        width="7.75rem"
      >
        <UpdateFeedbackStatus
          feedback={feedback}
          size="XS"
          emphasis="low"
          onSuccess={onSurveyUpdated}
          showAsBadge={!canUpdateStatus}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Referenza"
        width="12rem"
      >
        {prospect && <ProspectPreview prospect={prospect} />}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Punteggio"
        width="3rem"
      >
        {feedback.survey?.submittedAt && (
          <ShortRating score={feedback.survey.score} />
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Data di compilazione"
        width={CELL_DATE_WIDTH}
      >
        {feedback.survey?.submittedAt && (
          <DetailStack
            label="Compilata"
            aria-label="Data di compilazione survey"
          >
            <DateViewer
              stringDate={feedback.survey.submittedAt}
              checkIfToday
              withHour
            />
          </DetailStack>
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack gap={75} wrap="nowrap">
          {canSeeFeedback && [FeedbackStatus.COMPLETED, FeedbackStatus.REVIEWED].includes(feedback.status) && (
            <ViewFeedbackAction
              feedback={feedback}
              size="S"
              onSuccess={onSurveyUpdated}
              canEdit
            />
          )}
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewFeedbackTableRow;
